import { useEffect, useState } from "react";

import Box from "@mui/material/Box";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";

import { useSelector } from "react-redux";
import { withTranslation } from "react-i18next";

import PlanScope from "@plannel/pages/factory-plan/common/component/PlanScope";
import SearchOptsGroup from "@plannel/pages/factory-plan/common/component/SearchOptsGroup";
import FilterContainer from "@plannel/components/layout/FilterContainer";
import reduxUtil from "@plannel/utils/redux-util";

import BorTab from "./BorTab";
import BorSetTab from "./BorSetTab";
import ToolSettingTab from "./ToolSettingTab";
import ToolSupplyTab from "./ToolSupplyTab";

// 이 화면의 고정 조회조건 3개(§10 — DB 동적 표시 제어 제거, 라벨은 화면이 이미 알고 있는 고정 값).
const SEARCH_FIELD_LABELS = { routeCdNm: "FP_ROUTE", resourceCdNm: "FP_RESOURCE", itemCdNm: "FP_ITEM" };

// old Bor.jsx의 tabSrhOptsInfo 대응 — 탭마다 노출할 조회조건 필드만 다르고, 조회조건 폼 자체는
// 부모가 한 번만 렌더링(§11-2). 탭 전환은 노출 필드만 바꿀 뿐 재조회를 트리거하지 않는다(§11-3).
const TABS = [
  { key: "fpBor", label: "FP_BOR", Component: BorTab, searchFields: ["routeCdNm", "resourceCdNm", "itemCdNm"] },
  { key: "fpBorSet", label: "FP_BOR_SET", Component: BorSetTab, searchFields: ["routeCdNm", "resourceCdNm", "itemCdNm"] },
  { key: "fpToolSetting", label: "FP_TOOL_SETTING", Component: ToolSettingTab, searchFields: ["routeCdNm", "resourceCdNm"] },
  { key: "fpToolSupply", label: "FP_TOOL_SUPPLY", Component: ToolSupplyTab, searchFields: ["resourceCdNm"] },
];

const Bor = ({ t, viewName }) => {
  // 이 화면은 다른 Master 화면과 달리 viewState 영속화(탭 전환 시 필터 유지)가 원래 없었다 — 그
  // 부분은 이번 변경 범위 밖. 여기서는 validation/Validation의 딥링크 수신만 최소로 추가한다.
  const reduxViewState = reduxUtil.getViewState(viewName);

  const [planScope, setPlanScope] = useState(reduxViewState?.planScope ?? "");
  const [searchFields, setSearchFields] = useState({
    routeCdNm: reduxViewState?.routeCdNm ?? "",
    resourceCdNm: reduxViewState?.resourceCdNm ?? "",
    itemCdNm: reduxViewState?.itemCdNm ?? "",
  });
  const [tabIndex, setTabIndex] = useState(0);

  const onSearchFieldChange = (tpCd, value) => setSearchFields(prev => ({ ...prev, [tpCd]: value }));

  // item/Item.js와 동일한 딥링크 수신 로직(historyState 감지 → viewState 재조회) — 상세 설명은 그쪽 주석 참고.
  const incomingNav = useSelector((state) => state.historyState.history);
  useEffect(() => {
    if (incomingNav !== viewName) return;
    const freshState = reduxUtil.getViewState(viewName);
    if (!freshState) return;

    if (freshState.planScope) setPlanScope(freshState.planScope);
    setSearchFields((prev) => ({
      routeCdNm: freshState.routeCdNm ?? prev.routeCdNm,
      resourceCdNm: freshState.resourceCdNm ?? prev.resourceCdNm,
      itemCdNm: freshState.itemCdNm ?? prev.itemCdNm,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [incomingNav]);

  const activeTabFields = TABS[tabIndex].searchFields;

  return (
    <>
      <FilterContainer>
        <PlanScope value={planScope} onChange={setPlanScope} />
        <SearchOptsGroup
          fields={searchFields}
          fieldKeys={activeTabFields}
          onChange={onSearchFieldChange}
          labels={SEARCH_FIELD_LABELS}
        />
        {/* FilterContainer는 마지막 자식을 float:right(+필터 토글 아이콘)로 띄운다 — SearchOptsGroup이
            마지막이면 PlanScope와 큰 간격이 생기므로, 빈 Box를 마지막에 둬 그 역할을 넘긴다. */}
        <Box />
      </FilterContainer>
      <Tabs
        value={tabIndex}
        onChange={(_, v) => setTabIndex(v)}
        variant="scrollable"
        scrollButtons="auto"
        sx={{ minHeight: 38 }}
      >
        {TABS.map(tab => <Tab label={t(tab.label)} key={tab.key} sx={{ minHeight: 38, py: 0.75, fontSize: "0.875rem" }} />)}
      </Tabs>
      {/* §11-1: 탭은 항상 마운트하고 display로만 숨긴다 — 그리드 dirty-row/스크롤/선택 상태 보존.
          display:"flex"+flexDirection:"column"+flex:1 로 둬야 #tab-body(flex column)의 남는
          높이를 그대로 이어받아 내부 .grid-area{height:100%}가 정상 동작한다(block+calc()은
          FilterContainer/Tabs 높이가 조금만 바뀌어도 깨지는 매직넘버라 쓰지 않는다). */}
      {TABS.map((tab, index) => (
        <Box key={tab.key} sx={{ display: tabIndex === index ? "flex" : "none", flexDirection: "column", flex: 1, minHeight: 0 }}>
          <tab.Component viewName={`${viewName}_${tab.key}`} planScope={planScope} searchFields={searchFields} />
        </Box>
      ))}
    </>
  );
};

export default withTranslation()(Bor);
